import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { userApi } from '../api/user.api';
import { getDashboardRoute } from '../utils/roleHelpers';
import { syncDraftUsername } from '../utils/draftStorage';
import { invalidateMenuPreferencesCache } from '../utils/menuPreferences';

// get_profile_info is the one endpoint checkLogin() gates with no role
// restriction, so a 403 there always means "session actually invalid" —
// unlike role-scoped endpoints (e.g. get_unreaded_response_message, which is
// manager_employee-only), which 403 with the identical body for a merely
// wrong-role, still-valid session. Re-checking it periodically is the only
// reliable way to detect a mid-session expiry and log the user out.
const SESSION_CHECK_MS = 60000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(undefined);
  const [loading, setLoading] = useState(true);

  // Sync draft scope BEFORE React re-renders children with the new user,
  // otherwise DraftTextarea mounts under 'anon' and reply drafts appear
  // broken ("پیش‌نویس کار نمی‌کند") after login / account switch.
  const setUser = useCallback((next) => {
    const profile = typeof next === 'function' ? next(user) : next;
    if (profile?.username) syncDraftUsername(profile.username);
    else if (profile === null) syncDraftUsername(null);
    setUserState(next);
  }, [user]);

  const loadProfile = useCallback(() => {
    return userApi.getProfileInfo()
      .then(res => {
        const data = res.data;
        const profile = Array.isArray(data) ? data[0] : data;
        if (profile?.username) syncDraftUsername(profile.username);
        invalidateMenuPreferencesCache();
        setUserState(profile);
        return profile;
      })
      .catch(() => {
        syncDraftUsername(null);
        setUserState(null);
        return null;
      });
  }, []);

  useEffect(() => {
    loadProfile().finally(() => setLoading(false));
  }, [loadProfile]);

  useEffect(() => {
    if (!user) return;
    const id = setInterval(loadProfile, SESSION_CHECK_MS);
    return () => clearInterval(id);
  }, [user, loadProfile]);

  const logout = useCallback(async (navigate) => {
    try {
      const { authApi } = await import('../api/auth.api');
      await authApi.logout();
    } catch {}
    syncDraftUsername(null);
    invalidateMenuPreferencesCache();
    setUserState(null);
    navigate('/');
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, setLoading, loadProfile, logout, getDashboardRoute }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
